import React, {useState} from "react";
import './index.sass';
import {getTranslator} from "../../../translate.js";
import {Button, ButtonTypes} from "../../../react/components/ui/spectre/buttons.jsx";
import {travianCurrentServer} from "../../../travianCore.js";
import Api from "../../../api/api.js";
import {FreeOasis} from "./freeOasis.jsx";
import {decorateOasisData} from "./decorateOasisData.jsx";

const translate = getTranslator('tool/trav/24');

const sortFunc = (a, b) => {
    // 1. Farms that have a scan result go over farms that need scanning
    // 2. Within each group, farms with a positive outcome are on top
    // 3. Within each subgroup, farms that are being attacked are on the bottom
    // 4a. Each subgroup with positive outcome is sorted by PUT score
    // 4b. Each subgroup without positive outcome is sorted by distance

    if ((a.needsScan || b.needsScan) && !(a.needsScan && b.needsScan)) {
        if (b.needsScan) {
            return -1;
        } else {
            return 1;
        }
    }

    if ((a.hasPositiveOutcome || b.hasPositiveOutcome) && !(a.hasPositiveOutcome && b.hasPositiveOutcome)) {
        if (b.hasPositiveOutcome) {
            return 1;
        } else {
            return -1;
        }
    }

    const aIsAttacked = (a.lastAttackSent !== null && a.lastAttackSent > (a.lastAttackTime || a.time));
    const bIsAttacked = (b.lastAttackSent !== null && b.lastAttackSent > (b.lastAttackTime || b.time));

    if ((aIsAttacked || bIsAttacked) && !(aIsAttacked && bIsAttacked)) {
        if (bIsAttacked) {
            return -1;
        } else {
            return 1;
        }
    }

    if (!(a.hasPositiveOutcome || b.hasPositiveOutcome)) {
        return a.distance - b.distance;
    } else {
        return b.bestProfitPUT.score - a.bestProfitPUT.score;
    }
}

export default function ToolTrav24() {
    let [ data, setData ] = useState(null);
    const [ ownX, setOwnX ] = useState(156);
    const [ ownY, setOwnY ] = useState(148);
    const [ ownTroops, setOwnTroops ] = useState(1600);

    async function reload() {
        const data = await Api.get('userData/travian/oasis/')
        const parsedData = Object.values(data)
            .map(k => ({...k, selected: false}))
            .map(datum => decorateOasisData(ownX, ownY, datum))
            .sort(sortFunc);
        setData(parsedData);
    }

    if (data === null) {
        reload().then();
        data = [];
    }

    function onCheckboxChange(id, checked) {
        const newData = data.filter(k => k.id !== id);
        const newRow = data.filter(k => k.id === id)[0];
        if (newRow.selected === checked) {
            return;
        }
        newData.push({
            ...newRow,
            selected: checked,
        });
        newData.sort(sortFunc);
        setData(newData);
    }

    function openOldForScan(amount) {
        data
            .filter(k => k.needsScan).slice(0, amount)
            .map(oasis => `https://${travianCurrentServer}/position_details.php?x=${oasis.x}&y=${oasis.y}`)
            .forEach(url => window.open(url));
    }

    function openForAttack(amount) {
        const lastAttackThreshold = Date.now() / 1000 - 6 * 3600;

        const toOpen = data
            .filter(datum =>
                datum.hasPositiveOutcome
                && datum.bestProfitPUT.units > 10
                && datum.lastAttack < lastAttackThreshold
                && (datum.lastAttackSent === null || datum.lastAttackSent < datum.lastAttack || datum.lastAttackSent < datum.time)
            )
            .sort((a, b) => b.bestProfitPUT.score - a.bestProfitPUT.score);
        let units = ownTroops, amountOpened = 0;
        const oasisSent = [];
        for (let oasis of toOpen) {
            if (oasis.bestProfitPUT.units > units) {
                continue;
            }

            window.open(oasis.linkToAttack);
            units -= oasis.bestProfitPUT.units;
            oasisSent.push(oasis.id);

            amountOpened++;
            if (units <= 10 || amountOpened >= amount) {
                break;
            }
        }
        const newData = data.map(k => ({ ...k, selected: oasisSent.indexOf(k.id) !== -1 }));
        setData(newData);
    }

    async function markAsSent() {
        const args = {
            oasisIds: data
                .filter(oasis => oasis.selected)
                .map(oasis => oasis.id)
        };
        await Api.put('userData/travian/oasis/markAttackSent', args);
        await reload();
    }

    return <div>
        <div className="settingsContainer">
            <Button
                buttonTypes={[ButtonTypes.primary]}
                onClick={() => openOldForScan(50)}
            >
                {translate('scan_now')}
            </Button>
            <Button
                buttonTypes={[ButtonTypes.primary]}
                onClick={() => openForAttack(25)}
            >
                {translate('raid_now')}
            </Button>
            <Button buttonTypes={[ButtonTypes.primary]}
                disableDuringOnClick={true}
                onClick={() => reload().then()}
            >
                {translate('reload')}
            </Button>
            <Button buttonTypes={[ButtonTypes.primary]}
                disableDuringOnClick={true}
                onClick={() => markAsSent().then()}
            >
                {translate('mark_sent')}
            </Button>
            <div className="ownVillageCoordinates">
                {translate('own_coordinates')}:
                (
                <input type="number" min={-400} max={400} step={1} value={ownX}
                       onChange={e => setOwnX(parseInt(e.target.value))} />
                |
                <input type="number" min={-400} max={400} step={1} value={ownY}
                       onChange={e => setOwnY(parseInt(e.target.value))} />
                )
            </div>

            <div className="ownTroops">
                {translate('own_troops')}
                <input type="number" value={ownTroops}
                       onChange={e => setOwnTroops(parseInt(e.target.value))} />
                Steppe Riders
            </div>
        </div>

        <div className="freeOasisContainer">
            <div className="freeOasisHeader">
                <div></div>
                <div className="coordinateHeader">
                    {translate('coordinate_header')}
                </div>
                <div></div>
                <div className='pl-2'>{translate('data_from')}</div>
                <div>{translate('troops')}</div>
                <div>{translate('last_attack')}</div>
                <div className='tooltip tooltip-top' data-tooltip={translate('total_resources')}>{translate('bounty')}</div>
                <div className='tooltip tooltip-top' data-tooltip={translate('total_resources')}>{translate('bounty_hero')}</div>
                <div></div>
                <div></div>
                <div></div>
            </div>
            {
                data.map(oasis =>
                    <FreeOasis
                        key={oasis.id}
                        oasisData={oasis}
                        onCheckboxChange={onCheckboxChange}
                        ownX={ownX}
                        ownY={ownY}
                        ownTroops={ownTroops}
                    />
                )
            }
        </div>
    </div>
}
